import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Button, Table, Form, Dropdown, Offcanvas, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { PropagateLoader } from "react-spinners";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaBox,
  FaPlus,
  FaEdit,
  FaTrash,
  FaDownload,
  FaImage,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaUpload,
  FaArrowLeft,
  FaEye,
  FaCaretDown,
  FaTable,
  FaThLarge,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Cookies from "universal-cookie";
import { useTranslate } from "../hooks/LanguageProvider";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

const SUPPLIER_PRODUCTS_IMAGE_URL =
  "https://admin.aida-dz.xyz/API/uploads/supplier-products/";

type ProductImage = string;

interface Product {
  id_product: number;
  product_name: string;
  category: string;
  material: string;
  annual_capacity: number;
  unit: string;
  constructor_name?: string;
  requested_capacity?: number | null;
  currently_imported?: number;
  import_substitution?: number;
  homologation_status?: string;
  export_status?: string;
  product_images?: ProductImage[] | string;
}

interface NewImage {
  file: File;
  preview: string;
}

const emptyForm = {
  product_name: "",
  category: "",
  material: "",
  annual_capacity: "",
  unit: "pcs/year",
  constructor_name: "",
  requested_capacity: "",
  currently_imported: "0",
  import_substitution: "0",
  homologation_status: "",
  export_status: "",
};

const normalizeImages = (value: Product["product_images"]): string[] => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const cleanValue = value.trim();

    if (!cleanValue) return [];

    try {
      const parsed = JSON.parse(cleanValue);

      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      return cleanValue
        .split("|||")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
};

const getImageUrl = (path?: string | null) => {
  if (!path) return "";

  const filename = String(path).trim().split("/").pop();

  if (!filename) return "";

  return `${SUPPLIER_PRODUCTS_IMAGE_URL}${filename}`;
};

const productTextKeys = {
  all: "all",
  products: "products",
  addProduct: "add_product",
  editProduct: "edit_product",
  saveInProgress: "saving",
  cancel: "cancel",
  save: "save",
  backToSupplierProfile: "back_to_supplier_profile",
  product: "product",
  productName: "product_name",
  category: "category",
  material: "material",
  annualCapacity: "annual_capacity",
  capacity: "capacity",
  unit: "unit",
  constructor: "constructor",
  requestedCapacity: "requested_capacity",
  capacityGap: "capacity_gap",
  capacityStatus: "capacity_status",
  capacityStatusOk: "capacity_status_ok",
  capacityStatusKo: "capacity_status_ko",
  imported: "imported",
  substitution: "substitution",
  homologation: "homologation",
  export: "export",
  actions: "actions",
  photo: "photo",
  sheetView: "cards",
  tableView: "table",
  notProvided: "not_provided",
  noProducts: "no_products",
  displaying: "displaying",
  outOf: "out_of",
  yes: "yes",
  no: "no",
  selected: "selected_items",
  selectDisplayed: "select_displayed_products",
  selectProduct: "select_product",
  displayColumns: "Display columns",
  resetColumns: "reset_columns",
  searchPlaceholder: "search_product_placeholder",
  details: "details",
  capacityBlock: "capacity",
  qualification: "qualification",
  imagesOnly: "images_only",
  deleteSelection: "delete_selection",
  selectionDeleted: "selection_deleted",
  selectionDeleteError: "selection_delete_error",
  affectedConstructor: "affected_constructor",
  requestedCapacityFull: "requested_capacity_full",
  importedCurrently: "imported_currently",
  keptOldPhotos: "kept_old_photos",
  newPhotos: "new_photos",
  addPhotos: "add_photos",
  productPhotos: "product_photos",
  productThumbnail: "product_thumbnail",
  validated: "validated",
  refused: "refused",
  inProgress: "in_progress",
  required: "required",
  previous: "previous",
  next: "next",
  loadProductsError: "load_products_error",
  productNameRequired: "product_name_required",
  deleteSelectionConfirm: "delete_selection_confirm",
  deleteProductConfirm: "delete_product_confirm",
  deleteProductError: "delete_product_error",
  clickToAddPhotos: "click_to_add_photos",
  oneOrMoreImages: "one_or_more_images",
} as const;

type ProductText = Record<keyof typeof productTextKeys, string>;
type TranslateFn = (key: string) => string;

const buildProductText = (t: TranslateFn): ProductText => {
  return Object.fromEntries(
    Object.entries(productTextKeys).map(([name, key]) => [name, t(key)])
  ) as ProductText;
};

const displayValue = (value: unknown, emptyText: string) => {
  if (value === null || value === undefined || value === "") return emptyText;
  return String(value);
};

const normalizeDisplayValue = (value: unknown, emptyText: string) => {
  const text = displayValue(value, emptyText).trim();
  const normalized = text.toLowerCase().replace(/[\s-]+/g, "_");

  const displayMap: Record<string, string> = {
    to_qualify: "A qualifier",
    to_assess: "A evaluer",
    export_ready: "Pret export",
  };

  return displayMap[normalized] || text;
};

const formatYesNo = (
  value: unknown,
  yesText: string,
  noText: string
) => {
  if (
    value === 1 ||
    value === "1" ||
    value === true ||
    value === "true" ||
    value === "Oui" ||
    value === "oui"
  ) {
    return yesText;
  }

  return noText;
};

const getCapacityGap = (annual?: number, requested?: number | null) => {
  const a = Number(annual || 0);
  const r = Number(requested || 0);

  if (!r) return null;

  return a - r;
};

const getCapacityStatus = (
  annual?: number,
  requested?: number | null,
  text?: ProductText
) => {
  const gap = getCapacityGap(annual, requested);

  if (!text) return "";
  if (gap === null) return text.notProvided;

  return gap >= 0 ? text.capacityStatusOk : text.capacityStatusKo;
};

const getCapacityBadgeClass = (status: string, text: ProductText) => {
  if (status === text.capacityStatusOk) {
    return "sp-status-badge is-ok";
  }

  if (status === text.capacityStatusKo) {
    return "sp-status-badge is-ko";
  }

  return "sp-status-badge is-missing";
};

const normalizeSearchText = (value: unknown) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export default function SupplierProducts() {
  const { translate } = useTranslate();
  const t = translate;
  const navigate = useNavigate();
  const cookies = useMemo(() => new Cookies(), []);
  const productText = useMemo(() => buildProductText(t), [t]);
  const showValue = (value: unknown) => normalizeDisplayValue(value, productText.notProvided);
  const yesNo = (value: unknown) => formatYesNo(value, productText.yes, productText.no);
  const translatedCapacityStatus = (annual?: number, requested?: number | null) =>
    getCapacityStatus(annual, requested, productText);
  const isMissingText = (value: string) => normalizeSearchText(value) === normalizeSearchText(productText.notProvided);
  const renderTooltipCell = (value: unknown, className = "") => {
    const text = showValue(value);
    const tone = isMissingText(text) ? "is-missing" : "";

    return (
      <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
        <span className={`sp-cell-limit ${tone} ${className}`.trim()} title={text}>
          {text}
        </span>
      </OverlayTrigger>
    );
  };
  const renderBooleanChip = (value: unknown) => {
    const label = yesNo(value);
    const tone = label === productText.yes ? "is-yes" : "is-no";

    return <span className={`sp-boolean-chip ${tone}`}>{label}</span>;
  };
  const searchFields = useMemo(
    () => [
      { key: "all", label: productText.all },
      { key: "product_name", label: productText.product },
      { key: "category", label: productText.category },
      { key: "material", label: productText.material },
      { key: "constructor_name", label: productText.constructor },
      { key: "homologation_status", label: productText.homologation },
      { key: "export_status", label: productText.export },
    ],
    [productText]
  );

  const [searchParams] = useSearchParams();
  const supplierIdFromUrl = searchParams.get("id_user");

  const id_user =
    supplierIdFromUrl ??
    localStorage.getItem("userid") ??
    localStorage.getItem("id_user") ??
    localStorage.getItem("GeopUserID") ??
    "";

  const productColumns = useMemo(
    () => [
      { key: "photo", label: productText.photo, className: "sp-col-photo", minWidth: 78 },
      { key: "product", label: productText.product, className: "sp-col-product", minWidth: 320 },
      { key: "category", label: productText.category, className: "sp-col-category", minWidth: 160 },
      { key: "material", label: productText.material, className: "sp-col-material", minWidth: 160 },
      { key: "annualCapacity", label: productText.annualCapacity, className: "sp-col-medium", minWidth: 145 },
      { key: "unit", label: productText.unit, className: "sp-col-small", minWidth: 105 },
      { key: "constructor", label: productText.constructor, className: "sp-col-constructor", minWidth: 160 },
      { key: "requestedCapacity", label: productText.requestedCapacity, className: "sp-col-medium", minWidth: 145 },
      { key: "capacityGap", label: productText.capacityGap, className: "sp-col-medium", minWidth: 145 },
      { key: "capacityStatus", label: productText.capacityStatus, className: "sp-col-medium", minWidth: 145 },
      { key: "imported", label: productText.imported, className: "sp-col-small", minWidth: 105 },
      { key: "substitution", label: productText.substitution, className: "sp-col-small", minWidth: 105 },
      { key: "homologation", label: productText.homologation, className: "sp-col-medium", minWidth: 145 },
      { key: "export", label: productText.export, className: "sp-col-medium", minWidth: 145 },
    ],
    [productText]
  );
  const columnCookieKey = `supplier-products-hidden-columns-${id_user || "default"}`;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const visibleColumns = useMemo(
    () => productColumns.filter((column) => !hiddenColumns.includes(column.key)),
    [hiddenColumns, productColumns]
  );
  const isColumnVisible = (key: string) => !hiddenColumns.includes(key);
  const tableMinWidth = Math.max(
    620,
    42 + 112 + visibleColumns.reduce((total, column) => total + column.minWidth, 0)
  );

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const [form, setForm] = useState(emptyForm);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [newImages, setNewImages] = useState<NewImage[]>([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function loadProducts() {
    if (!id_user) return;

    setLoading(true);

    try {
      const res = await fetch(`${backendUrl}/api/supplier-products/${id_user}`);
      const data = await res.json();

      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(productText.loadProductsError);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_user]);

  useEffect(() => {
    const savedColumns = cookies.get(columnCookieKey);
    const validKeys = new Set(productColumns.map((column) => column.key));

    if (Array.isArray(savedColumns)) {
      setHiddenColumns(savedColumns.filter((key) => validKeys.has(String(key))).map(String));
      return;
    }

    if (typeof savedColumns === "string") {
      try {
        const parsed = JSON.parse(savedColumns);
        setHiddenColumns(
          Array.isArray(parsed) ? parsed.filter((key) => validKeys.has(String(key))).map(String) : []
        );
      } catch {
        setHiddenColumns([]);
      }
    }
  }, [columnCookieKey, cookies, productColumns]);

  useEffect(() => {
    return () => {
      newImages.forEach((img) => URL.revokeObjectURL(img.preview));
    };
  }, [newImages]);

  const allDrawerImages = useMemo(() => {
    const oldImages = existingImages.map((path) => ({
      type: "old",
      path,
      preview: getImageUrl(path),
    }));

    const uploadedImages = newImages.map((img) => ({
      type: "new",
      path: img.file.name,
      preview: img.preview,
    }));

    return [...oldImages, ...uploadedImages];
  }, [existingImages, newImages]);

  const filtered = useMemo(() => {
    const text = normalizeSearchText(search);

    if (!text) return products;

    return products.filter((p) => {
      if (searchField === "all") {
        return searchFields
          .filter((field) => field.key !== "all")
          .some((field) => normalizeSearchText((p as any)[field.key]).includes(text));
      }

      return normalizeSearchText((p as any)[searchField]).includes(text);
    });
  }, [products, search, searchField, searchFields]);

  const pageCount = Math.ceil(filtered.length / limit) || 1;
  const current = filtered.slice(page * limit, page * limit + limit);

  const currentIds = current.map((p) => p.id_product);

  const allCurrentSelected =
    currentIds.length > 0 && currentIds.every((id) => selectedIds.has(id));

  const productsForExport =
    selectedIds.size > 0
      ? products.filter((p) => selectedIds.has(p.id_product))
      : filtered;

  const exportColumns = useMemo(() => {
    const columns = visibleColumns.filter((column) => column.key !== "photo");
    return columns.length ? columns : productColumns.filter((column) => column.key === "product");
  }, [productColumns, visibleColumns]);

  const getExportValue = (product: Product, key: string) => {
    switch (key) {
      case "product":
        return showValue(product.product_name);
      case "category":
        return showValue(product.category);
      case "material":
        return showValue(product.material);
      case "annualCapacity":
        return showValue(product.annual_capacity);
      case "unit":
        return showValue(product.unit);
      case "constructor":
        return showValue(product.constructor_name);
      case "requestedCapacity":
        return showValue(product.requested_capacity);
      case "capacityGap":
        return showValue(getCapacityGap(product.annual_capacity, product.requested_capacity));
      case "capacityStatus":
        return translatedCapacityStatus(product.annual_capacity, product.requested_capacity);
      case "imported":
        return yesNo(product.currently_imported);
      case "substitution":
        return yesNo(product.import_substitution);
      case "homologation":
        return showValue(product.homologation_status);
      case "export":
        return showValue(product.export_status);
      default:
        return "";
    }
  };

  const selectedData = productsForExport.map((product) =>
    exportColumns.reduce<Record<string, string>>((row, column) => {
      row[column.label] = String(getExportValue(product, column.key));
      return row;
    }, {})
  );

  function resetForm() {
    setEditId(null);
    setForm(emptyForm);
    setExistingImages([]);

    setNewImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.preview));
      return [];
    });

    setActiveImageIndex(0);
    setDrawerOpen(false);
  }

  function openCreateDrawer() {
    resetForm();
    setDrawerOpen(true);
  }

  function openEditDrawer(product: Product) {
    resetForm();

    setEditId(product.id_product);

    setForm({
      product_name: product.product_name || "",
      category: product.category || "",
      material: product.material || "",
      annual_capacity:
        product.annual_capacity !== null && product.annual_capacity !== undefined
          ? String(product.annual_capacity)
          : "",
      unit: product.unit || "pcs/year",
      constructor_name: product.constructor_name || "",
      requested_capacity:
        product.requested_capacity !== null && product.requested_capacity !== undefined
          ? String(product.requested_capacity)
          : "",
      currently_imported: String(product.currently_imported || 0),
      import_substitution: String(product.import_substitution || 0),
      homologation_status: product.homologation_status || "",
      export_status: product.export_status || "",
    });

    setExistingImages(normalizeImages(product.product_images));
    setDrawerOpen(true);
  }

  function handleInput(name: string, value: string) {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  function handleFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    const remainingSlots = Math.max(0, 10 - existingImages.length - newImages.length);

    const images = files
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, remainingSlots)
      .map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

    if (!images.length) {
      toast.warning(productText.imagesOnly);
      return;
    }

    setNewImages((prev) => [...prev, ...images]);
    setActiveImageIndex(existingImages.length + newImages.length);

    e.target.value = "";
  }

  function removeImage(index: number) {
    const oldCount = existingImages.length;

    if (index < oldCount) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newIndex = index - oldCount;

      setNewImages((prev) => {
        const item = prev[newIndex];

        if (item) {
          URL.revokeObjectURL(item.preview);
        }

        return prev.filter((_, i) => i !== newIndex);
      });
    }

    setActiveImageIndex((prev) => Math.max(0, prev - 1));
  }

  function nextImage() {
    if (!allDrawerImages.length) return;

    setActiveImageIndex((prev) => (prev + 1) % allDrawerImages.length);
  }

  function previousImage() {
    if (!allDrawerImages.length) return;

    setActiveImageIndex((prev) => (prev - 1 + allDrawerImages.length) % allDrawerImages.length);
  }

  function buildFormData() {
    const data = new FormData();

    data.append("id_user", id_user);

    Object.entries(form).forEach(([key, value]) => {
      data.append(key, value);
    });

    data.set("annual_capacity", String(Number(form.annual_capacity) || 0));
    data.set(
      "requested_capacity",
      form.requested_capacity ? String(Number(form.requested_capacity)) : ""
    );
    data.set("currently_imported", String(Number(form.currently_imported) || 0));
    data.set("import_substitution", String(Number(form.import_substitution) || 0));

    data.append("existing_images", JSON.stringify(existingImages));

    newImages.forEach((img) => {
      data.append("photos", img.file);
    });

    return data;
  }

  async function saveProduct() {
    if (!form.product_name.trim()) {
      toast.warning(productText.productNameRequired);
      return;
    }

    setSaving(true);

    try {
      const url = editId
        ? `${backendUrl}/api/supplier-products/update/${editId}`
        : `${backendUrl}/api/supplier-products/create`;

      const method = editId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        body: buildFormData(),
      });

      if (!res.ok) throw new Error("Erreur serveur");

      toast.success(editId ? t("product_updated") : t("product_added"));

      resetForm();
      loadProducts();
    } catch (error) {
      toast.error(editId ? t("error_updating_product") : t("error_adding_product"));
    } finally {
      setSaving(false);
    }
  }

  function toggleSelectProduct(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  function saveHiddenColumns(nextHiddenColumns: string[]) {
    setHiddenColumns(nextHiddenColumns);
    cookies.set(columnCookieKey, nextHiddenColumns, {
      path: "/",
      maxAge: 60 * 60 * 24 * 180,
      sameSite: "lax",
    });
  }

  function toggleColumn(key: string) {
    const nextHiddenColumns = hiddenColumns.includes(key)
      ? hiddenColumns.filter((columnKey) => columnKey !== key)
      : [...hiddenColumns, key];

    saveHiddenColumns(nextHiddenColumns);
  }

  function resetColumns() {
    saveHiddenColumns([]);
  }

  function toggleSelectCurrentPage() {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (allCurrentSelected) {
        currentIds.forEach((id) => next.delete(id));
      } else {
        currentIds.forEach((id) => next.add(id));
      }

      return next;
    });
  }

  async function deleteSelectedProducts() {
    const ids = Array.from(selectedIds);

    if (!ids.length) return;

    if (!window.confirm(`${productText.deleteSelectionConfirm} (${ids.length}) ?`)) {
      return;
    }

    try {
      await Promise.all(
        ids.map((id) =>
          fetch(`${backendUrl}/api/supplier-products/${id}`, {
            method: "DELETE",
          })
        )
      );

      toast.success(productText.selectionDeleted);
      setSelectedIds(new Set());
      loadProducts();
    } catch {
      toast.error(productText.selectionDeleteError);
    }
  }

  async function deleteProduct(id: number) {
    if (!window.confirm(productText.deleteProductConfirm)) return;

    try {
      await fetch(`${backendUrl}/api/supplier-products/${id}`, {
        method: "DELETE",
      });

      toast.success(t("product_deleted"));

      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      loadProducts();
    } catch {
      toast.error(productText.deleteProductError);
    }
  }

  function exportExcel() {
    if (!selectedData.length) {
      toast.warning(productText.noProducts);
      return;
    }

    const ws = XLSX.utils.json_to_sheet(selectedData);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.writeFile(wb, "products.xlsx");
  }

  function exportCSV() {
    if (!selectedData.length) {
      toast.warning(productText.noProducts);
      return;
    }

    const ws = XLSX.utils.json_to_sheet(selectedData);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "products.csv";
    a.click();

    window.URL.revokeObjectURL(url);
  }

  function exportPDF() {
    if (!productsForExport.length) {
      toast.warning(productText.noProducts);
      return;
    }

    const doc = new jsPDF();

    autoTable(doc, {
      head: [exportColumns.map((column) => column.label)],
      body: productsForExport.map((product) =>
        exportColumns.map((column) => String(getExportValue(product, column.key)))
      ),
    });

    doc.save("products.pdf");
  }

  const activeImage = allDrawerImages[activeImageIndex];
  const activeSearchField =
    searchFields.find((field) => field.key === searchField)?.label || productText.all;

  function goBackToSupplierProfile() {
    if (id_user) {
      navigate(`/profileSupplier/${id_user}`);
    } else {
      navigate("/profileSupplier");
    }
  }

  return (
    <div className="container-fluid supplier-products-page">
      <style>{`
        .supplier-products-page {
          padding: 18px 22px;
          overflow-x: hidden;
        }

        .sp-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
          width: 100%;
        }

        .sp-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }

        .sp-title-block {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          min-width: 0;
          flex: 1 1 auto;
        }

        .sp-header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          flex: 0 0 auto;
          margin-left: auto;
          flex-wrap: wrap;
        }

        .sp-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          box-shadow: 0 8px 28px rgba(15, 23, 42, .06);
          overflow: hidden;
        }

        .sp-toolbar {
          display: flex;
          align-items: stretch;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 14px;
          border-bottom: 1px solid #eef2f7;
          flex-wrap: nowrap;
        }

        .sp-toolbar-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: nowrap;
          justify-content: flex-end;
          flex: 0 0 auto;
        }

        .sp-selection-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }

        .sp-check-cell {
          width: 42px;
          min-width: 42px;
          max-width: 42px;
          text-align: center;
          vertical-align: middle;
          padding: 0 8px !important;
        }

        .sp-check-cell .form-check {
          min-height: 0;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sp-check-cell .form-check-input {
          position: static !important;
          margin: 0;
          cursor: pointer;
          width: 15px;
          height: 15px;
        }

        .sp-search {
          width: min(760px, 100%);
          max-width: none;
          flex: 0 1 760px;
          min-width: 0;
        }

        .sp-search.input-group {
          display: flex;
          flex-wrap: nowrap;
          align-items: stretch;
        }

        .sp-search .dropdown {
          flex: 0 0 auto;
        }

        .sp-search .dropdown-toggle {
          min-width: 92px;
          height: 38px;
          padding-top: 6px;
          padding-bottom: 6px;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          border-right: 0;
        }

        .sp-search .form-control {
          min-width: 0;
          width: 100%;
          flex: 1 1 auto;
          height: 38px;
          min-height: 38px;
          padding-top: 6px;
          padding-bottom: 6px;
        }

        .sp-page-size {
          width: 110px;
          min-width: 110px;
        }

        .sp-columns-menu {
          max-height: 360px;
          overflow-y: auto;
          min-width: 240px;
          padding: 8px;
        }

        .sp-columns-menu .dropdown-item {
          border-radius: 8px;
          padding: 7px 8px;
        }

        .sp-columns-menu .form-check {
          display: flex;
          align-items: center;
          gap: 8px;
          min-height: 22px;
          margin: 0;
          color: #1f2937;
          font-size: 13px;
          font-weight: 600;
        }

        .sp-columns-menu .form-check-input {
          position: static !important;
          margin: 0;
          cursor: pointer;
        }

        .sp-columns-toggle.dropdown-toggle {
          min-width: 46px;
          min-height: 38px;
          padding: 6px 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          color: #0f172a;
          border-color: #cbd5e1;
          background: #fff;
        }

        .sp-columns-toggle.dropdown-toggle::after {
          display: none;
        }

        .sp-columns-toggle svg {
          color: #0f172a;
        }

        .sp-view-switch {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          background: #f8fafc;
        }

        .sp-view-switch button {
          border: 0;
          min-height: 31px;
          padding: 5px 10px;
          border-radius: 8px;
          background: transparent;
          color: #475569;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
        }

        .sp-view-switch button.active {
          background: #111827;
          color: #fff;
          box-shadow: 0 6px 14px rgba(15, 23, 42, .16);
        }

        .sp-product-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 12px;
          padding: 14px;
          min-height: 360px;
          background: #f8fafc;
        }

        .sp-product-card {
          display: grid;
          grid-template-columns: 42px 74px minmax(0, 1fr);
          gap: 12px;
          padding: 12px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          background: #fff;
          box-shadow: 0 8px 20px rgba(15, 23, 42, .05);
          min-width: 0;
        }

        .sp-product-card.selected {
          border-color: #f59e0b;
          box-shadow: 0 10px 24px rgba(245, 158, 11, .16);
        }

        .sp-product-card-check {
          display: flex;
          justify-content: center;
          padding-top: 26px;
        }

        .sp-product-card-check .form-check-input {
          position: static !important;
          margin: 0;
          cursor: pointer;
        }

        .sp-product-card-media {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }

        .sp-product-card-media .sp-thumb,
        .sp-product-card-media .sp-thumb-empty {
          width: 68px;
          height: 68px;
          border-radius: 12px;
        }

        .sp-product-card-body {
          min-width: 0;
        }

        .sp-product-card-title {
          margin: 0;
          color: #0f172a;
          font-size: 15px;
          font-weight: 700;
          line-height: 1.35;
          overflow-wrap: anywhere;
        }

        .sp-product-card-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin: 8px 0 10px;
        }

        .sp-product-chip {
          display: inline-flex;
          align-items: center;
          max-width: 100%;
          padding: 4px 8px;
          border-radius: 999px;
          background: #eef2ff;
          color: #3730a3;
          font-size: 12px;
          font-weight: 600;
          overflow-wrap: anywhere;
        }

        .sp-product-chip.subtle {
          background: #ecfeff;
          color: #155e75;
        }

        .sp-product-field-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }

        .sp-product-field {
          min-width: 0;
          padding: 8px;
          border: 1px solid #eef2f7;
          border-radius: 10px;
          background: #f8fafc;
        }

        .sp-product-field-label {
          display: block;
          margin-bottom: 2px;
          color: #64748b;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .sp-product-field-value {
          color: #111827;
          font-size: 13px;
          font-weight: 600;
          overflow-wrap: anywhere;
        }

        .sp-product-card-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          margin-top: 10px;
        }

        .sp-empty-state {
          grid-column: 1 / -1;
          min-height: 240px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          font-weight: 600;
        }

        .sp-table-wrap {
          overflow-x: auto;
          overflow-y: auto;
          min-height: 360px;
          scrollbar-gutter: stable;
        }

        .sp-table {
          margin-bottom: 0;
          min-width: 1480px;
          table-layout: fixed;
        }

        .sp-table thead th {
          position: sticky;
          top: 0;
          background: #f8fafc;
          color: #0f172a;
          font-size: 13px;
          border-bottom: 1px solid #e5e7eb;
          z-index: 1;
          white-space: nowrap;
        }

        .sp-table tbody td {
          vertical-align: middle;
          font-size: 13px;
          color: #0f172a !important;
          overflow: hidden;
          white-space: normal;
        }

        .sp-cell-limit {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          max-height: 38px;
          line-height: 19px;
          overflow: hidden;
          color: #0f172a !important;
          font-weight: 700;
          word-break: break-word;
        }

        .sp-cell-limit.is-missing {
          display: inline-flex;
          align-items: center;
          width: fit-content;
          max-width: 100%;
          min-height: 24px;
          padding: 3px 9px;
          border-radius: 999px;
          background: #fff7ed;
          border: 1px solid #fed7aa;
          color: #9a3412;
          font-weight: 700;
          line-height: 16px;
          -webkit-line-clamp: unset;
          -webkit-box-orient: unset;
        }

        .sp-cell-limit.compact {
          -webkit-line-clamp: 1;
          max-height: 19px;
          white-space: nowrap;
          text-overflow: clip;
        }

        .sp-cell-limit.compact.is-missing {
          max-height: none;
          white-space: normal;
        }

        .sp-status-badge,
        .sp-boolean-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 26px;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 800;
          line-height: 1;
          white-space: nowrap;
        }

        .sp-status-badge.is-ok,
        .sp-boolean-chip.is-yes {
          background: #ecfdf3;
          border: 1px solid #bbf7d0;
          color: #166534;
        }

        .sp-status-badge.is-ko {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
        }

        .sp-status-badge.is-missing {
          background: #fff7ed;
          border: 1px solid #fed7aa;
          color: #9a3412;
        }

        .sp-boolean-chip.is-no {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #1d4ed8;
        }

        .sp-col-photo {
          width: 78px;
        }

        .sp-col-product {
          width: 320px;
        }

        .sp-col-category,
        .sp-col-material,
        .sp-col-constructor {
          width: 160px;
        }

        .sp-col-small {
          width: 105px;
        }

        .sp-col-medium {
          width: 145px;
        }

        .sp-action-cell {
          width: 112px;
          min-width: 112px;
          position: sticky;
          right: 0;
          background: #fff;
          z-index: 2;
          box-shadow: -8px 0 14px rgba(15, 23, 42, .06);
        }

        .sp-table thead .sp-action-cell {
          background: #f8fafc;
          z-index: 3;
        }

        .sp-thumb {
          width: 54px;
          height: 54px;
          border-radius: 10px;
          object-fit: cover;
          border: 1px solid #e5e7eb;
          background: #f8fafc;
          cursor: pointer;
          transition: transform .15s ease, box-shadow .15s ease;
        }

        .sp-thumb:hover {
          transform: scale(1.04);
          box-shadow: 0 8px 18px rgba(15, 23, 42, .14);
        }

        .sp-thumb-empty {
          width: 54px;
          height: 54px;
          border-radius: 10px;
          border: 1px dashed #cbd5e1;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          cursor: pointer;
        }

        .offcanvas.sp-drawer {
          width: min(880px, calc(100vw - 110px)) !important;
          max-width: calc(100vw - 110px);
          border-left: 1px solid #e5e7eb;
          box-shadow: -18px 0 45px rgba(15, 23, 42, .18);
        }

        .sp-drawer-header {
          border-bottom: 1px solid #e5e7eb;
          padding: 18px 22px;
          background: #fff;
        }

        .sp-drawer-body {
          background: #f8fafc;
          padding: 18px;
          overflow-x: hidden;
          overflow-y: auto;
        }

        .sp-editor {
          display: grid;
          grid-template-columns: 300px minmax(0, 1fr);
          gap: 18px;
          align-items: start;
          width: 100%;
          max-width: 100%;
        }

        .sp-photo-panel,
        .sp-form-panel {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 14px;
          min-width: 0;
          box-shadow: 0 10px 24px rgba(15, 23, 42, .05);
        }

        .sp-form-panel .row {
          --bs-gutter-x: 12px;
        }

        .sp-form-panel .form-label {
          font-size: 13px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 6px;
        }

        .sp-form-panel .form-control,
        .sp-form-panel .form-select {
          min-height: 42px;
          border-radius: 10px;
        }

        .sp-dropzone {
          height: 250px;
          border: 2px dashed #f59e0b;
          border-radius: 16px;
          background: #fffbeb;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          cursor: pointer;
          overflow: hidden;
          position: relative;
        }

        .sp-dropzone:hover {
          background: #fff7ed;
        }

        .sp-preview-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          background: #fff;
        }

        .sp-empty-photo {
          color: #92400e;
          padding: 18px;
        }

        .sp-empty-photo svg {
          font-size: 38px;
          color: #f59e0b;
          margin-bottom: 10px;
        }

        .sp-photo-actions {
          position: absolute;
          inset: auto 10px 10px 10px;
          display: flex;
          justify-content: space-between;
          pointer-events: none;
        }

        .sp-photo-actions button {
          pointer-events: auto;
          border: 0;
          width: 36px;
          height: 36px;
          border-radius: 999px;
          background: rgba(15, 23, 42, .78);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sp-remove-active {
          position: absolute;
          top: 10px;
          right: 10px;
          border: 0;
          width: 34px;
          height: 34px;
          border-radius: 999px;
          background: rgba(220, 38, 38, .9);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sp-miniatures {
          display: flex;
          gap: 8px;
          overflow: auto;
          margin-top: 12px;
          padding-bottom: 4px;
        }

        .sp-miniature {
          width: 62px;
          height: 62px;
          border-radius: 12px;
          object-fit: cover;
          border: 2px solid transparent;
          cursor: pointer;
          background: #fff;
        }

        .sp-miniature.active {
          border-color: #f59e0b;
        }

        .sp-upload-btn {
          width: 100%;
          margin-top: 12px;
        }

        .sp-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 14px 22px;
          border-top: 1px solid #e5e7eb;
          background: #fff;
          position: sticky;
          bottom: 0;
          z-index: 3;
        }

        @media (max-width: 760px) {
          .supplier-products-page {
            padding: 14px 12px;
          }

          .sp-title {
            justify-content: center;
            font-size: 22px;
          }

          .offcanvas.sp-drawer {
            width: 100vw !important;
            max-width: 100vw;
          }

          .sp-editor {
            grid-template-columns: 1fr;
          }

          .sp-toolbar,
          .sp-header {
            flex-direction: column;
            align-items: stretch;
          }

          .sp-title-block {
            justify-content: center;
          }

          .sp-header-actions {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            width: 100%;
            margin-left: 0;
          }

          .sp-header-actions > .btn:first-child {
            grid-column: 1 / -1;
          }

          .sp-header-actions .dropdown,
          .sp-header-actions .dropdown-toggle,
          .sp-header-actions > .btn {
            width: 100%;
            min-height: 42px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            white-space: normal;
            line-height: 1.2;
          }

          .sp-toolbar {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            padding: 12px;
          }

          .sp-search {
            grid-column: 1 / -1;
            width: 100%;
            flex: 1 1 auto;
          }

          .sp-search.input-group {
            display: grid;
            grid-template-columns: 78px minmax(0, 1fr);
          }

          .sp-search .dropdown-toggle {
            width: 100%;
            min-width: 0;
            height: 40px;
            padding-left: 10px;
            padding-right: 10px;
          }

          .sp-search .form-control {
            font-size: 13px;
            height: 40px;
            min-height: 40px;
            padding-left: 10px;
            padding-right: 10px;
          }

          .sp-toolbar-actions {
            display: contents;
          }

          .sp-toolbar-actions > * {
            width: 100%;
          }

          .sp-view-switch {
            grid-column: 1 / -1;
            justify-content: center;
          }

          .sp-product-list {
            grid-template-columns: 1fr;
            padding: 10px;
          }

          .sp-product-card {
            grid-template-columns: 32px 62px minmax(0, 1fr);
            gap: 10px;
            padding: 10px;
          }

          .sp-product-card-media .sp-thumb,
          .sp-product-card-media .sp-thumb-empty {
            width: 58px;
            height: 58px;
          }

          .sp-product-field-grid {
            grid-template-columns: 1fr;
          }

          .sp-selection-badge {
            justify-content: center;
            min-height: 42px;
            border-radius: 999px;
            white-space: nowrap;
            padding-left: 10px;
            padding-right: 10px;
          }

          .sp-toolbar-actions .btn {
            min-height: 42px;
            white-space: normal;
            line-height: 1.2;
          }

          .sp-page-size {
            width: 100%;
            min-width: 0;
            min-height: 42px;
          }

          .sp-card {
            border-radius: 14px;
          }

          .sp-table-wrap {
            min-height: 380px;
          }

          .sp-table {
            min-width: 960px;
          }

          .sp-check-cell {
            width: 36px;
            min-width: 36px;
            max-width: 36px;
          }

          .sp-action-cell {
            width: 96px;
            min-width: 96px;
          }

          .sp-action-cell .btn {
            padding: 4px 7px;
          }

          .supplier-products-page .border-top {
            flex-direction: column;
            gap: 12px;
            align-items: stretch !important;
          }

          .supplier-products-page .pagination {
            justify-content: center !important;
            flex-wrap: nowrap;
            overflow-x: auto;
            padding-bottom: 2px;
          }
        }
      `}</style>

      <div className="sp-header">
        <div className="sp-title-block">
          <h4 className="sp-title">
            <FaBox /> {productText.products} <Badge bg="secondary">{products.length}</Badge>
          </h4>
        </div>

        <div className="sp-header-actions">
          {supplierIdFromUrl && (
            <Button variant="outline-secondary" onClick={goBackToSupplierProfile}>
              <FaArrowLeft className="me-1" /> {productText.backToSupplierProfile}
            </Button>
          )}

          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary">
              <FaDownload className="me-1" /> {productText.export}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item onClick={exportExcel}>Excel</Dropdown.Item>
              <Dropdown.Item onClick={exportCSV}>CSV</Dropdown.Item>
              <Dropdown.Item onClick={exportPDF}>PDF</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Button variant="warning" onClick={openCreateDrawer}>
            <FaPlus className="me-1" /> {productText.addProduct}
          </Button>
        </div>
      </div>

      <div className="sp-card">
        <div className="sp-toolbar">
          <div className="input-group sp-search">
            <Dropdown>
              <Dropdown.Toggle variant="light">{activeSearchField}</Dropdown.Toggle>

              <Dropdown.Menu>
                {searchFields.map((field) => (
                  <Dropdown.Item
                    key={field.key}
                    active={searchField === field.key}
                    onClick={() => {
                      setSearchField(field.key);
                      setPage(0);
                    }}
                  >
                    {field.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <Form.Control
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder={productText.searchPlaceholder}
            />
          </div>

          <div className="sp-toolbar-actions">
            <div className="sp-view-switch" aria-label="Mode d'affichage">
              <button
                type="button"
                className={viewMode === "cards" ? "active" : ""}
                onClick={() => setViewMode("cards")}
              >
                <FaThLarge /> {productText.sheetView}
              </button>

              <button
                type="button"
                className={viewMode === "table" ? "active" : ""}
                onClick={() => setViewMode("table")}
              >
                <FaTable /> {productText.tableView}
              </button>
            </div>

            {selectedIds.size > 0 && (
              <>
                <span className="sp-selection-badge badge bg-warning text-dark">
                  {selectedIds.size} {productText.selected}
                </span>

                <Button size="sm" variant="outline-danger" onClick={deleteSelectedProducts}>
                  {productText.deleteSelection}
                </Button>
              </>
            )}

            <Dropdown autoClose="outside">
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                className="sp-columns-toggle"
                title={productText.displayColumns}
                aria-label={productText.displayColumns}
              >
                <FaEye />
                <FaCaretDown size={12} />
              </Dropdown.Toggle>

              <Dropdown.Menu className="sp-columns-menu">
                {productColumns.map((column) => (
                  <Dropdown.Item
                    as="button"
                    key={column.key}
                    onClick={(event) => {
                      event.preventDefault();
                      toggleColumn(column.key);
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={isColumnVisible(column.key)}
                      readOnly
                      label={column.label}
                    />
                  </Dropdown.Item>
                ))}

                <Dropdown.Divider />

                <Dropdown.Item as="button" onClick={resetColumns}>
                  {productText.resetColumns}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Form.Select
              className="sp-page-size"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(0);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </Form.Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center p-5">
            <PropagateLoader />
          </div>
        ) : viewMode === "cards" ? (
          <div className="sp-product-list">
            {current.length ? (
              current.map((p) => {
                const imgs = normalizeImages(p.product_images);
                const firstImage = imgs[0] || "";
                const imageUrl = getImageUrl(firstImage);
                const gap = getCapacityGap(p.annual_capacity, p.requested_capacity);
                const status = translatedCapacityStatus(p.annual_capacity, p.requested_capacity);

                return (
                  <article
                    key={p.id_product}
                    className={`sp-product-card ${selectedIds.has(p.id_product) ? "selected" : ""}`}
                  >
                    <div className="sp-product-card-check">
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.has(p.id_product)}
                        onChange={() => toggleSelectProduct(p.id_product)}
                        aria-label={`${productText.selectProduct} ${p.product_name}`}
                      />
                    </div>

                    <div className="sp-product-card-media">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={p.product_name}
                          title={p.product_name}
                          className="sp-thumb"
                          onClick={() => openEditDrawer(p)}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="sp-thumb-empty" onClick={() => openEditDrawer(p)}>
                          <FaImage />
                        </div>
                      )}

                      {imgs.length > 1 && (
                        <Badge bg="dark">
                          +{imgs.length - 1}
                        </Badge>
                      )}
                    </div>

                    <div className="sp-product-card-body">
                      <h5 className="sp-product-card-title">{showValue(p.product_name)}</h5>

                      <div className="sp-product-card-meta">
                        <span className="sp-product-chip">{showValue(p.category)}</span>
                        <span className="sp-product-chip subtle">{showValue(p.material)}</span>
                      </div>

                      <div className="sp-product-field-grid">
                        <div className="sp-product-field">
                          <span className="sp-product-field-label">{productText.annualCapacity}</span>
                          <span className="sp-product-field-value">
                            {showValue(p.annual_capacity)} {showValue(p.unit)}
                          </span>
                        </div>

                        <div className="sp-product-field">
                          <span className="sp-product-field-label">{productText.requestedCapacity}</span>
                          <span className="sp-product-field-value">
                            {showValue(p.requested_capacity)}
                          </span>
                        </div>

                        <div className="sp-product-field">
                          <span className="sp-product-field-label">{productText.capacityGap}</span>
                          <span className="sp-product-field-value">
                            {gap ?? productText.notProvided}
                          </span>
                        </div>

                        <div className="sp-product-field">
                          <span className="sp-product-field-label">{productText.capacityStatus}</span>
                          <span className={getCapacityBadgeClass(status, productText)}>{status}</span>
                        </div>

                        <div className="sp-product-field">
                          <span className="sp-product-field-label">{productText.constructor}</span>
                          <span className="sp-product-field-value">
                            {showValue(p.constructor_name)}
                          </span>
                        </div>

                        <div className="sp-product-field">
                          <span className="sp-product-field-label">{productText.imported}</span>
                          <span className="sp-product-field-value">
                            {renderBooleanChip(p.currently_imported)}
                          </span>
                        </div>

                        <div className="sp-product-field">
                          <span className="sp-product-field-label">{productText.substitution}</span>
                          <span className="sp-product-field-value">
                            {renderBooleanChip(p.import_substitution)}
                          </span>
                        </div>

                        <div className="sp-product-field">
                          <span className="sp-product-field-label">{productText.homologation}</span>
                          <span className="sp-product-field-value">
                            {showValue(p.homologation_status)}
                          </span>
                        </div>

                        <div className="sp-product-field">
                          <span className="sp-product-field-label">{productText.export}</span>
                          <span className="sp-product-field-value">
                            {showValue(p.export_status)}
                          </span>
                        </div>
                      </div>

                      <div className="sp-product-card-actions">
                        <Button
                          size="sm"
                          variant="outline-warning"
                          onClick={() => openEditDrawer(p)}
                        >
                          <FaEdit />
                        </Button>

                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => deleteProduct(p.id_product)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="sp-empty-state">{productText.noProducts}</div>
            )}
          </div>
        ) : (
          <div className="sp-table-wrap">
            <Table hover responsive className="sp-table" style={{ minWidth: tableMinWidth }}>
              <thead>
                <tr>
                  <th className="sp-check-cell">
                    <Form.Check
                      type="checkbox"
                      checked={allCurrentSelected}
                      disabled={!current.length}
                      onChange={toggleSelectCurrentPage}
                      aria-label={productText.selectDisplayed}
                    />
                  </th>
                  {visibleColumns.map((column) => (
                    <th key={column.key} className={column.className}>
                      {column.label}
                    </th>
                  ))}
                  <th className="text-center sp-action-cell">{productText.actions}</th>
                </tr>
              </thead>

              <tbody>
                {current.length ? (
                  current.map((p) => {
                    const imgs = normalizeImages(p.product_images);
                    const firstImage = imgs[0] || "";
                    const imageUrl = getImageUrl(firstImage);
                    const gap = getCapacityGap(p.annual_capacity, p.requested_capacity);
                    const status = translatedCapacityStatus(p.annual_capacity, p.requested_capacity);

                    return (
                      <tr key={p.id_product}>
                        <td className="sp-check-cell">
                          <Form.Check
                            type="checkbox"
                            checked={selectedIds.has(p.id_product)}
                            onChange={() => toggleSelectProduct(p.id_product)}
                            aria-label={`${productText.selectProduct} ${p.product_name}`}
                          />
                        </td>

                        {isColumnVisible("photo") && (
                          <td>
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={p.product_name}
                                title={p.product_name}
                                className="sp-thumb"
                                onClick={() => openEditDrawer(p)}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";

                                  const parent = e.currentTarget.parentElement;

                                  if (parent && !parent.querySelector(".sp-thumb-empty")) {
                                    const fallback = document.createElement("div");
                                    fallback.className = "sp-thumb-empty";
                                    fallback.innerHTML = "&#128444;&#65039;";
                                    fallback.onclick = () => openEditDrawer(p);
                                    parent.prepend(fallback);
                                  }
                                }}
                              />
                            ) : (
                              <div className="sp-thumb-empty" onClick={() => openEditDrawer(p)}>
                                <FaImage />
                              </div>
                            )}

                            {imgs.length > 1 && (
                              <Badge bg="dark" className="ms-1">
                                +{imgs.length - 1}
                              </Badge>
                            )}
                          </td>
                        )}

                        {isColumnVisible("product") && <td>{renderTooltipCell(p.product_name)}</td>}
                        {isColumnVisible("category") && <td>{renderTooltipCell(p.category)}</td>}
                        {isColumnVisible("material") && <td>{renderTooltipCell(p.material)}</td>}
                        {isColumnVisible("annualCapacity") && <td>{renderTooltipCell(p.annual_capacity, "compact")}</td>}
                        {isColumnVisible("unit") && <td>{renderTooltipCell(p.unit, "compact")}</td>}
                        {isColumnVisible("constructor") && <td>{renderTooltipCell(p.constructor_name)}</td>}
                        {isColumnVisible("requestedCapacity") && <td>{renderTooltipCell(p.requested_capacity, "compact")}</td>}
                        {isColumnVisible("capacityGap") && <td>{renderTooltipCell(gap ?? productText.notProvided, "compact")}</td>}
                        {isColumnVisible("capacityStatus") && (
                          <td>
                            <span className={getCapacityBadgeClass(status, productText)}>{status}</span>
                          </td>
                        )}
                        {isColumnVisible("imported") && <td>{renderBooleanChip(p.currently_imported)}</td>}
                        {isColumnVisible("substitution") && <td>{renderBooleanChip(p.import_substitution)}</td>}
                        {isColumnVisible("homologation") && <td>{renderTooltipCell(p.homologation_status, "compact")}</td>}
                        {isColumnVisible("export") && <td>{renderTooltipCell(p.export_status, "compact")}</td>}

                        <td className="text-center sp-action-cell">
                          <Button
                            size="sm"
                            variant="outline-warning"
                            className="me-2"
                            onClick={() => openEditDrawer(p)}
                          >
                            <FaEdit />
                          </Button>

                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => deleteProduct(p.id_product)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={visibleColumns.length + 2} className="text-center py-5">
                      {productText.noProducts}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center p-3 border-top">
          <span>
            {productText.displaying} {current.length} {productText.outOf} {filtered.length}
            {selectedIds.size > 0 ? ` - ${selectedIds.size} ${productText.selected}` : ""}
          </span>

          <ReactPaginate
            previousLabel={productText.previous}
            nextLabel={productText.next}
            breakLabel="..."
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={(d) => setPage(d.selected)}
            containerClassName="pagination justify-content-end mb-0"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            activeClassName="active"
            forcePage={Math.min(page, pageCount - 1)}
          />
        </div>
      </div>

      <Offcanvas show={drawerOpen} onHide={resetForm} placement="end" className="sp-drawer">
        <Offcanvas.Header closeButton className="sp-drawer-header">
          <Offcanvas.Title>{editId ? productText.editProduct : productText.addProduct}</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="sp-drawer-body">
          <div className="sp-editor">
            <div className="sp-photo-panel">
              <strong>{productText.productPhotos}</strong>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleFiles}
              />

              <div className="sp-dropzone mt-3" onClick={openFilePicker}>
                {activeImage ? (
                  <>
                    <img
                      src={activeImage.preview}
                      alt={form.product_name || "Produit"}
                      title={form.product_name || "Produit"}
                      className="sp-preview-img"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />

                    <button
                      type="button"
                      className="sp-remove-active"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(activeImageIndex);
                      }}
                    >
                      <FaTimes />
                    </button>

                    {allDrawerImages.length > 1 && (
                      <div className="sp-photo-actions">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            previousImage();
                          }}
                        >
                          <FaChevronLeft />
                        </button>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            nextImage();
                          }}
                        >
                          <FaChevronRight />
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="sp-empty-photo">
                    <FaUpload />
                    <div className="fw-bold">{productText.clickToAddPhotos}</div>
                    <small>{productText.oneOrMoreImages}</small>
                  </div>
                )}
              </div>

              <Button variant="outline-warning" className="sp-upload-btn" onClick={openFilePicker}>
                <FaImage className="me-2" />
                {productText.addPhotos}
              </Button>

              {allDrawerImages.length > 0 && (
                <div className="sp-miniatures">
                  {allDrawerImages.map((img, index) => (
                    <img
                      key={`${img.path}-${index}`}
                      src={img.preview}
                      alt={form.product_name || productText.productThumbnail}
                      title={form.product_name || productText.productThumbnail}
                      className={`sp-miniature ${index === activeImageIndex ? "active" : ""}`}
                      onClick={() => setActiveImageIndex(index)}
                      onError={(e) => {
                        e.currentTarget.style.opacity = "0.35";
                      }}
                    />
                  ))}
                </div>
              )}

              <div className="text-muted small mt-2">
                {productText.keptOldPhotos} : {existingImages.length} | {productText.newPhotos} :{" "}
                {newImages.length}
              </div>
            </div>

            <div className="sp-form-panel">
              <Form>
                <div className="row">
                  <Form.Group className="mb-3 col-md-12">
                    <Form.Label>{productText.productName}</Form.Label>
                    <Form.Control
                      value={form.product_name}
                      onChange={(e) => handleInput("product_name", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>{productText.category}</Form.Label>
                    <Form.Control
                      value={form.category}
                      onChange={(e) => handleInput("category", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>{productText.material}</Form.Label>
                    <Form.Control
                      value={form.material}
                      onChange={(e) => handleInput("material", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>{productText.annualCapacity}</Form.Label>
                    <Form.Control
                      type="number"
                      value={form.annual_capacity}
                      onChange={(e) => handleInput("annual_capacity", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>{productText.unit}</Form.Label>
                    <Form.Control
                      value={form.unit}
                      onChange={(e) => handleInput("unit", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-12">
                    <Form.Label>{productText.affectedConstructor}</Form.Label>
                    <Form.Control
                      value={form.constructor_name}
                      onChange={(e) => handleInput("constructor_name", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-12">
                    <Form.Label>{productText.requestedCapacityFull}</Form.Label>
                    <Form.Control
                      type="number"
                      value={form.requested_capacity}
                      onChange={(e) => handleInput("requested_capacity", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>{productText.importedCurrently}</Form.Label>
                    <Form.Select
                      value={form.currently_imported}
                      onChange={(e) => handleInput("currently_imported", e.target.value)}
                    >
                      <option value="0">{productText.no}</option>
                      <option value="1">{productText.yes}</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>{productText.substitution}</Form.Label>
                    <Form.Select
                      value={form.import_substitution}
                      onChange={(e) => handleInput("import_substitution", e.target.value)}
                    >
                      <option value="0">{productText.no}</option>
                      <option value="1">{productText.yes}</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>{productText.homologation}</Form.Label>
                    <Form.Select
                      value={form.homologation_status}
                      onChange={(e) => handleInput("homologation_status", e.target.value)}
                    >
                      <option value="">{productText.notProvided}</option>
                      <option value="Validée">{productText.validated}</option>
                      <option value="En cours">{productText.inProgress}</option>
                      <option value="Requise">{productText.required}</option>
                      <option value="Refusée">{productText.refused}</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>{productText.export}</Form.Label>
                    <Form.Select
                      value={form.export_status}
                      onChange={(e) => handleInput("export_status", e.target.value)}
                    >
                      <option value="">{productText.notProvided}</option>
                      <option value="Export-ready">Export-ready</option>
                      <option value="Potentiel">Potentiel</option>
                      <option value="Non exportable">Non exportable</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </Form>
            </div>
          </div>
        </Offcanvas.Body>

        <div className="sp-footer">
          <Button variant="secondary" onClick={resetForm} disabled={saving}>
            {productText.cancel}
          </Button>

          <Button variant="warning" onClick={saveProduct} disabled={saving}>
            {saving ? productText.saveInProgress : productText.save}
          </Button>
        </div>
      </Offcanvas>
    </div>
  );
}
